import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { 
    isAuthenticated, 
    user: auth0User,
    loginWithRedirect, 
    logout,
    getAccessTokenSilently,
    isLoading 
  } = useAuth0();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Ajout : synchronisation stricte Auth0 <-> Supabase à chaque connexion
  const createOrUpdateUser = async (userData) => {
    try {
      // Recherche par email (clé unique)
      const { data: existingUser } = await supabase
        .from('users')
        .select('id, is_active')
        .eq('email', userData.email)
        .single();

      let userId = existingUser?.id;

      if (!existingUser) {
        // Création si inexistant
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert([{
            email: userData.email,
            full_name: userData.name || userData.email.split('@')[0],
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (insertError) throw insertError;
        userId = newUser.id;

        // Ajout d'un rôle par défaut si besoin
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: userId,
            role_id: 1
          });
        if (roleError) throw roleError;
      } else {
        // Mise à jour du nom si changé dans Auth0
        if (userData.name && userData.name !== existingUser.full_name) {
          await supabase
            .from('users')
            .update({ full_name: userData.name, updated_at: new Date().toISOString() })
            .eq('id', userId);
        }
        // Vérification du statut actif
        if (existingUser.is_active === false) {
          throw new Error('Compte désactivé');
        }
      }

      return userId;
    } catch (error) {
      throw error;
    }
  };

  const fetchUserRoles = async (email) => {
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          id,
          email,
          full_name,
          is_active,
          user_sectors (
            sectors (
              id,
              name,
              is_active
            )
          ),
          user_roles (
            role_id,
            roles (
              id,
              name,
              can_view_all_sectors,
              can_view_all_users,
              can_access_dashboard,
              can_access_time_entry,
              can_access_mandate_management,
              can_access_releve_code_management,
              can_access_sector_management,
              can_access_activities_admin,
              can_access_pointage_mensuel,
              can_access_rapport_mensuel,
              can_access_monthly_activities,
              can_access_user_admin,
              can_access_roles_admin,
              can_access_sector_calendar,
              can_manage_sector_calendar
            )
          )
        `)
        .eq('email', email)
        .single();

      if (userError) throw userError;

      if (!userData.is_active) {
        throw new Error('Compte désactivé');
      }

      const userObject = {
        id: userData.id,
        email: userData.email,
        full_name: userData.full_name,
        is_active: userData.is_active,
        role: userData.user_roles?.[0]?.roles?.name || 'Sans rôle',
        role_id: userData.user_roles?.[0]?.role_id,
        sectors: userData.user_sectors?.map(us => us.sectors).filter(s => s.is_active) || [],
        user_sectors: userData.user_sectors || [],
        user_roles: userData.user_roles || []
      };

      if (userData?.user_roles?.[0]?.roles) {
        const userRole = userData.user_roles[0].roles;
        const rolePermissions = {
          id: userRole.id,
          name: userRole.name,
          role_id: userData.user_roles[0].role_id,
          can_view_all_sectors: userRole.can_view_all_sectors || false,
          can_view_all_users: userRole.can_view_all_users || false,
          can_access_dashboard: userRole.can_access_dashboard || false,
          can_access_time_entry: userRole.can_access_time_entry || false,
          can_access_mandate_management: userRole.can_access_mandate_management || false,
          can_access_releve_code_management: userRole.can_access_releve_code_management || false,
          can_access_sector_management: userRole.can_access_sector_management || false,
          can_access_activities_admin: userRole.can_access_activities_admin || false,
          can_access_pointage_mensuel: userRole.can_access_pointage_mensuel || false,
          can_access_rapport_mensuel: userRole.can_access_rapport_mensuel || false,
          can_access_monthly_activities: userRole.can_access_monthly_activities || false,
          can_access_user_admin: userRole.can_access_user_admin || false,
          can_access_roles_admin: userRole.can_access_roles_admin || false,
          can_access_sector_calendar: userRole.can_access_sector_calendar || false,
          can_manage_sector_calendar: userRole.can_manage_sector_calendar || false
        };
        
        setUser(userObject);
        setRoles(rolePermissions);
        return rolePermissions;
      }

      return null;
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (!isLoading) {
        try {
          if (isAuthenticated && auth0User) {
            await getAccessTokenSilently();
            // Synchronisation Auth0 <-> Supabase
            await createOrUpdateUser(auth0User);
            const userRoles = await fetchUserRoles(auth0User.email);

            if (!userRoles) {
              throw new Error('Aucun rôle assigné');
            }

            const hasAnyPermission = Object.values(userRoles).some(permission => permission === true);
            if (!hasAnyPermission) {
              throw new Error('Aucune permission accordée');
            }

            if (location.pathname === '/login') {
              const returnTo = location.state?.returnTo || '/';
              navigate(returnTo, { replace: true });
            }
          } else if (!isAuthenticated && location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        } catch (error) {
          setUser(null);
          setRoles(null);
          navigate('/login', { replace: true });
        } finally {
          setAuthChecked(true);
        }
      }
    };

    checkAuth();
  }, [
    isLoading,
    isAuthenticated,
    auth0User,
    getAccessTokenSilently,
    location.pathname,
    location.state?.returnTo,
    navigate
  ]);

  const signIn = () => {
    loginWithRedirect({
      appState: { returnTo: location.pathname === '/login' ? '/' : location.pathname }
    });
  };

  const signOut = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (isLoading || !authChecked) {
    return <div>Chargement...</div>;
  }

  return (
    <AuthContext.Provider value={{ 
      user,
      roles,
      isAuthenticated: !!user,
      signIn,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
